import type React from "react";
import { useContext, useEffect, useState } from "react";
import "./TranslateSentencesOrallyActivity.scss";
import { BsFillMicFill } from "react-icons/bs";
import { BsArrowLeft, BsArrowRight } from "react-icons/bs";
import { GiSpeaker } from "react-icons/gi";
import { Radio } from "react-loader-spinner";
import { IconButton, Tag } from "rsuite";
import { LanguageContext } from "../../../app/context";
import type { Sentence } from "../../../entities/Activity/types";
import { useActivityCompletion } from "../../../hooks/useActivitiesCompletion";
import { Fireworks } from "../../../shared/components/Fireworks/Fireworks";
import { Stepper } from "../../../shared/components/Stepper/Stepper";
import { formClass, playSynthesisUtterance } from "../../../shared/helpers";
import {
	getLocaleByLanguage,
	getScoreMessage,
	stringMatchingPrecision,
} from "./helpers";
import type { SpeechRecognition } from "./types";
type Props = {
	data: Sentence[];
	activityId: string;
};

declare global {
	interface Window {
		SpeechRecognition: typeof SpeechRecognition;
		webkitSpeechRecognition: typeof SpeechRecognition;
	}
}

const TranslateSentencesOrallyActivity: React.FC<Props> = ({
	data,
	activityId,
}) => {
	const prefixClass = "translate-sentences-orally-activity";
	const [rotatedWordIndices, setRotatedWordIndices] = useState<{
		[key: number]: boolean;
	}>({});
	const [currentSentenceIndex, setCurrentSentenceIndex] = useState(0);
	const [isListening, setIsListening] = useState(false);
	const [fireworksTrigger, setFireworksTrigger] = useState(false);
	const [speechResult, setSpeechResult] = useState("");
	const [score, setScore] = useState<number>(0);
	const [resultIsLoading, setResultIsLoading] = useState(false);
	const [steps, setSteps] = useState(
		data.map((_, index) => {
			return {
				index,
				current: index === currentSentenceIndex,
				completed: false,
			};
		}),
	);

	const { language } = useContext(LanguageContext);

	const { triggerActivityCompletion } = useActivityCompletion();

	// biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
	useEffect(() => {
		setSteps(
			steps.map((steps, index) => {
				return {
					...steps,
					current: index === currentSentenceIndex,
				};
			}),
		);
	}, [currentSentenceIndex]);

	const toggleTranslation = (wordIndex: number) => {
		setRotatedWordIndices((prevState) => ({
			...prevState,
			[wordIndex]: !prevState[wordIndex],
		}));
	};

	const speechRecognitionStart = () => {
		const SpeechRecognition =
			window.SpeechRecognition || window.webkitSpeechRecognition;
		const recognition = new SpeechRecognition();
		recognition.lang = getLocaleByLanguage(language);

		recognition.onstart = () => {
			setIsListening(true);
		};

		recognition.onend = () => {
			setIsListening(false);
		};

		recognition.start();

		recognition.onresult = (event: {
			results: { [index: number]: { [index: number]: { transcript: string } } };
		}) => {
			const speechResult = event.results[0][0].transcript;
			setSpeechResult(speechResult);
			checkAnswer(speechResult);
		};
	};

	console.log({ score });

	const resetActivity = () => {
		setCurrentSentenceIndex(0);
		setSpeechResult("");
		setScore(0);
		setRotatedWordIndices({});
	};

	const checkAnswer = async (speechRes: string) => {
		setResultIsLoading(true);
		const currentSentence = data[currentSentenceIndex];
		const joinedSentence = currentSentence
			.map((word) => word.targetLangWord)
			.join(" ");
		const recognitionScore = stringMatchingPrecision(speechRes, joinedSentence);

		if (recognitionScore > 0.9) {
			setSteps((prev) => {
				const newSteps = [...prev];
				newSteps[currentSentenceIndex].completed = true;
				return newSteps;
			});
		} else {
			console.log("wrong");
			setSteps((prev) => {
				const newSteps = [...prev];
				newSteps[currentSentenceIndex].completed = false;
				return newSteps;
			});
		}

		const allCorrect = steps.every((step) => step.completed === true);
		if (allCorrect) {
			setFireworksTrigger(true);
			await triggerActivityCompletion(activityId, 16, resetActivity);
		}

		setScore(recognitionScore);
		setResultIsLoading(false);
	};

	const nextSentence = () => {
		if (currentSentenceIndex === data.length - 1) {
			return;
		}
		setCurrentSentenceIndex((prev) => prev + 1);
		setSpeechResult("");
		setScore(0);
		setRotatedWordIndices({});
	};

	const prevSentence = () => {
		if (currentSentenceIndex === 0) {
			return;
		}
		setCurrentSentenceIndex((prev) => prev - 1);
		setSpeechResult("");
		setScore(0);
		setRotatedWordIndices({});
	};

	const producePronunciation = (
		e: React.MouseEvent<HTMLButtonElement>,
		word: string,
	) => {
		e.stopPropagation();
		playSynthesisUtterance({
			word,
			language,
		});
	};

	const currentSentence = data[currentSentenceIndex];
	const isPrevDisabled = currentSentenceIndex === 0;
	const isNextDisabled = currentSentenceIndex === data.length - 1;

	return (
		<div className={prefixClass}>
			{fireworksTrigger && (
				<Fireworks
					trigger={fireworksTrigger}
					onComplete={() => setFireworksTrigger(false)}
				/>
			)}
			<div className={formClass(prefixClass, "stepper")}>
				<Stepper steps={steps} />
			</div>
			<div className={formClass(prefixClass, "speech-result-holder")}>
				{speechResult && (
					<>
						{`${speechResult.toUpperCase()}. `}
						{resultIsLoading ? (
							<Radio
								height="20"
								width="20"
								ariaLabel="audio-loading"
								wrapperStyle={{}}
								wrapperClass="wrapper-class"
								visible={true}
							/>
						) : (
							<>
								Your result is{" "}
								<Tag
									className={formClass(prefixClass, "tag")}
									color={getScoreMessage(score).color}
								>
									{getScoreMessage(score).message}
								</Tag>
							</>
						)}
					</>
				)}
			</div>
			<button
				type="button"
				onClick={speechRecognitionStart}
				className={`${formClass(prefixClass, "mic-btn")} ${isListening ? "is-listening" : ""}`}
			>
				<BsFillMicFill />
			</button>
			<div className={`${prefixClass}__sentence`}>
				{currentSentence?.map((word, wordIndex) => (
					<div
						className={`${prefixClass}__cube ${rotatedWordIndices[wordIndex] ? "is-flipped" : ""}`}
						key={word.sourceLangWord}
						onClick={() => toggleTranslation(wordIndex)}
						onKeyUp={(e) => {
							if (e.key === "Enter" || e.key === " ") {
								toggleTranslation(wordIndex);
							}
						}}
					>
						<div
							className={`${prefixClass}__cube__face ${prefixClass}__cube__face--front`}
						>
							<span>{word.sourceLangWord}</span>
						</div>
						<div
							className={`${prefixClass}__cube__face ${prefixClass}__cube__face--back`}
						>
							<button
								type="button"
								onClick={(e) => producePronunciation(e, word.targetLangWord)}
								className={formClass(prefixClass, "word-btn")}
							>
								<GiSpeaker className={formClass("audio-icon")} />
								<span>{word.targetLangWord}</span>
							</button>
						</div>
						<div
							className={`${prefixClass}__cube__face ${prefixClass}__cube__face--right`}
						/>
						<div
							className={`${prefixClass}__cube__face ${prefixClass}__cube__face--left`}
						/>
						<div
							className={`${prefixClass}__cube__face ${prefixClass}__cube__face--top`}
						/>
						<div
							className={`${prefixClass}__cube__face ${prefixClass}__cube__face--bottom`}
						/>
					</div>
				))}
			</div>
			<div className={formClass(prefixClass, "btn-group")}>
				<IconButton
					size="md"
					icon={<BsArrowLeft />}
					disabled={isPrevDisabled}
					onClick={() => prevSentence()}
				/>
				<IconButton
					size="md"
					icon={<BsArrowRight />}
					disabled={isNextDisabled}
					onClick={() => nextSentence()}
				/>
			</div>
		</div>
	);
};

export default TranslateSentencesOrallyActivity;
